import {
  ChevronRight,
  FolderOutlined,
  InsertChartOutlinedOutlined
} from '@mui/icons-material';
import {
  List,
  ListItemButton,
  ListItemButtonProps,
  ListProps,
  Stack,
  styled
} from '@mui/material';
import { useGetSessionInfo } from '@sentinel/hooks';
import { useAuth } from '@vestwell-frontend/elements';
import { formatCurrency } from '@vestwell-frontend/helpers';
import { useDocumentTitle, usePostMutation } from '@vestwell-frontend/hooks';
import { Loader, SuspenseImage, Text } from '@vestwell-frontend/ui';
import { usePortalMiddlewareGetAdvisorCommercialPlatforms } from '@vestwell-unified-api/hooks';

import { useCallback } from 'react';

import { useNav } from '../hooks';

const StyledList = styled(List)<ListProps>(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  border: `${theme.spacing(0.25)} solid ${theme.palette.border.main}`,
  borderRadius: theme.spacing(1),
  boxShadow: theme.shadows[24],
  marginBottom: theme.spacing(6),
  padding: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    maxWidth: theme.spacing(160)
  }
}));

const StyledLogo = styled(SuspenseImage)(({ theme }) => ({
  maxHeight: '100%',
  [theme.breakpoints.up('sm')]: {
    width: 'auto'
  },
  [theme.breakpoints.down('sm')]: {
    width: '70%'
  }
}));

const StyledListItemButton = styled(ListItemButton)<ListItemButtonProps>(
  ({ theme }) => ({
    '&:last-of-type': {
      borderBottom: `${theme.spacing(0.25)} solid transparent`
    },
    alignItems: 'center',
    borderBottom: `${theme.spacing(0.25)} solid ${theme.palette.gray20.main}`,
    display: 'flex',
    [theme.breakpoints.up('sm')]: {
      height: theme.spacing(21)
    },
    [theme.breakpoints.down('sm')]: {
      height: 'fit-content'
    },
    justifyContent: 'space-between',
    padding: theme.spacing(4)
  })
);

export function PlatformSelectionPage() {
  useDocumentTitle('Books of Business');

  const { setToken, isNative } = useAuth();

  const nav = useNav();

  const advisor = useGetSessionInfo('advisor');

  const commercialPlatforms = usePortalMiddlewareGetAdvisorCommercialPlatforms(
    { advisorId: advisor?.data?.advisorId, firmId: advisor?.data?.firmId },
    {
      query: {
        enabled: !!advisor?.data?.advisorId && !!advisor?.data?.firmId
      }
    }
  );

  const [postEntity, { isLoading }] = usePostMutation(
    '/auth/api/v1/login/entity',
    {},
    {
      onSuccess: async data => {
        setToken(data?.token);
        if (!isNative) {
          nav.external(data?.homeUrl);
        } else {
          return window.location.assign(
            `${window.location.protocol}//mobile.vestwell/advisor/index.html`
          );
        }
      }
    }
  );

  const onClick = useCallback(
    async (entity: {
      entityId: number;
      entityType: string;
      commercialPlatformId: number;
    }) => {
      await postEntity({ ...entity, hasPersona: true });
    },
    [commercialPlatforms?.data]
  );

  return isLoading ? (
    <Loader />
  ) : (
    <Stack alignItems='center' flexGrow={1} px={4} width='100%'>
      <Text
        align='center'
        color='neutral.dark'
        pb={2}
        pt={6}
        variant='c2'
        width='100%'>
        Books of Business
      </Text>
      {commercialPlatforms.data.map(platform => (
        <StyledList component='nav' key={platform.name}>
          <StyledListItemButton
            onClick={() => {
              onClick({
                commercialPlatformId: platform.commercialPlatformId,
                entityId: advisor.data.advisorId,
                entityType: 'advisor'
              });
            }}>
            <Stack
              alignItems={{ sm: 'center', xs: 'baseline' }}
              direction={{ sm: 'row', xs: 'column' }}
              gap={2}
              height='100%'
              justifyContent='space-between'
              pr={10}
              width='100%'>
              {platform.navbar?.logo?.desktop ? (
                <StyledLogo
                  alt={`${platform.name} logo`}
                  height='auto'
                  src={platform.navbar?.logo?.desktop}
                />
              ) : (
                <Text color='text.secondary' mb={0} variant='e1'>
                  {platform.name}
                </Text>
              )}
              <Stack
                alignItems={{ sm: 'center', xs: 'baseline' }}
                direction={{ sm: 'row', xs: 'column' }}
                width={{ sm: '40%', xs: '100%' }}>
                <Stack
                  alignItems='center'
                  color='white'
                  direction='row'
                  gap={1}
                  mb={{ sm: 0, xs: 3 }}
                  width={{ sm: '50%', xs: '100%' }}>
                  <FolderOutlined fontSize='small' />
                  <Text color='text.secondary' mb={0} variant='i1'>
                    {`${platform.numberOfSponsorPlans} ${platform.numberOfSponsorPlans > 1 ? 'plans' : 'plan'}`}
                  </Text>
                </Stack>
                <Stack
                  alignItems='center'
                  color='white'
                  direction='row'
                  gap={1}
                  width={{ sm: '50%', xs: '100%' }}>
                  <InsertChartOutlinedOutlined fontSize='small' />
                  <Text color='text.secondary' mb={0} variant='i1'>
                    {formatCurrency(platform.stats.totalBalance)}
                  </Text>
                </Stack>
              </Stack>
            </Stack>
            <ChevronRight color='primary' />
          </StyledListItemButton>
        </StyledList>
      ))}
    </Stack>
  );
}

PlatformSelectionPage.displayName = 'PlatformSelectionPage';
